import { convertToMonthlyAmount } from "../services/recurringTransactionService"
import type { FirestoreAccount, FirestoreExpense } from "../types/firestore"

export interface AccountFundingNeed {
  accountId: string
  accountName: string
  /** The account's planned monthly obligations (the funding target). */
  monthlyExpenses: number
  currentBalance: number
  /** monthlyExpenses - currentBalance; positive means "transfer this much". */
  shortfall: number
  needsTransfer: boolean
}

/**
 * "How much do I need to transfer into each account to cover its planned bills?"
 * — the question you ask on payday.
 *
 * The target is purely PLANNED: the sum of the account's active recurring
 * expense templates (`isRecurring`), each normalised to a monthly figure. Set
 * against the current balance:
 *
 *   plannedMonthly = active recurring expense templates, normalised to monthly
 *   shortfall      = plannedMonthly - balance   → transfer this much
 *
 * Actual bank-synced spending is deliberately NOT part of the maths. Money
 * that's already been spent has already left the account and is reflected in
 * the balance, so subtracting it too would double-count — an account with a
 * 5000 planned bill and a 865 balance is short 4135 no matter how much day-to-
 * day spending flowed through it this month.
 *
 * Accounts with no planned expenses have no target and are omitted (there's
 * nothing to "cover"); give an account a recurring template to make it appear.
 *
 * Templates reference an account by `accountId` OR `account` (name), so totals
 * are aggregated under both keys.
 */
export function computeAccountFundingNeeds(
  expenses: FirestoreExpense[],
  accounts: FirestoreAccount[],
): AccountFundingNeed[] {
  const forAccount = (map: Map<string, number>, account: { id: string; name: string }) =>
    (map.get(account.id) || 0) + (account.name && account.name !== account.id ? map.get(account.name) || 0 : 0)

  const plannedByAccount = new Map<string, number>()
  for (const template of expenses) {
    if (template.type !== "expense" || !template.isRecurring || template.isActive === false) continue
    const key = template.accountId || template.account
    if (!key || !template.frequency) continue
    const monthly = convertToMonthlyAmount(template.amount, template.frequency)
    plannedByAccount.set(key, (plannedByAccount.get(key) || 0) + monthly)
  }

  return accounts
    .map((account) => {
      // Work in whole kroner. A plan normalised to a monthly figure carries øre
      // (e.g. an annual bill / 12), so comparing it against a whole-krone bank
      // balance leaves a sub-krone shortfall that shows as "0 kr" yet still
      // flags the account as underfunded. Rounding both sides removes the noise.
      const target = Math.round(forAccount(plannedByAccount, account))
      const balance = Math.round(account.balance)
      const shortfall = target - balance
      return {
        accountId: account.id,
        accountName: account.name,
        monthlyExpenses: target,
        currentBalance: balance,
        shortfall,
        needsTransfer: shortfall > 0,
      }
    })
    .filter((need) => need.monthlyExpenses > 0)
    .sort((a, b) => b.shortfall - a.shortfall)
    .slice(0, 3)
}
